import { createClient } from '@/lib/supabase/client';

export interface DashboardStats {
  activeWorkers: number;
  activeSites: number;
  todayPresent: number;
  todayExpected: number;
  todayAbsent: number;
  todayLate: number;
  pendingPaymentsTotal: number;
  pendingPaymentsCount: number;
}

export interface SidebarBadges {
  workers: number;   // workers added in last 7 days
  attendance: number; // today's absent/unscanned workers
  payments: number;   // pending payment records
}

export const dashboardService = {
  async getStats(): Promise<DashboardStats> {
    const supabase = createClient();
    const today = new Date().toISOString().split('T')[0];

    const [workersRes, sitesRes, attendanceRes, paymentsRes] = await Promise.all([
      supabase
        .from('workers')
        .select('id', { count: 'exact', head: true })
        .eq('worker_status', 'active'),
      supabase
        .from('sites')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'active'),
      supabase
        .from('attendance_records')
        .select('attendance_status')
        .eq('attendance_date', today),
      supabase
        .from('payment_records')
        .select('net_pay')
        .eq('payment_status', 'pending'),
    ]);

    const activeWorkers = workersRes.count ?? 0;
    const activeSites = sitesRes.count ?? 0;

    const attendanceRows = attendanceRes.data ?? [];
    const todayPresent = attendanceRows.filter((r) => r.attendance_status === 'present').length;
    const todayLate = attendanceRows.filter((r) => r.attendance_status === 'late').length;
    const todayAbsent = attendanceRows.filter((r) => r.attendance_status === 'absent').length;
    const todayExpected = attendanceRows.length;

    const pendingRows = paymentsRes.data ?? [];
    const pendingPaymentsTotal = pendingRows.reduce((sum, r) => sum + Number(r.net_pay ?? 0), 0);
    const pendingPaymentsCount = pendingRows.length;

    return {
      activeWorkers,
      activeSites,
      todayPresent,
      todayExpected,
      todayAbsent,
      todayLate,
      pendingPaymentsTotal,
      pendingPaymentsCount,
    };
  },

  async getSidebarBadges(): Promise<SidebarBadges> {
    const supabase = createClient();
    const today = new Date().toISOString().split('T')[0];
    const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();

    const [newWorkersRes, absentTodayRes, pendingPayRes] = await Promise.all([
      supabase
        .from('workers')
        .select('id', { count: 'exact', head: true })
        .gte('created_at', sevenDaysAgo),
      supabase
        .from('attendance_records')
        .select('id', { count: 'exact', head: true })
        .eq('attendance_date', today)
        .eq('attendance_status', 'absent'),
      supabase
        .from('payment_records')
        .select('id', { count: 'exact', head: true })
        .eq('payment_status', 'pending'),
    ]);

    return {
      workers: newWorkersRes.count ?? 0,
      attendance: absentTodayRes.count ?? 0,
      payments: pendingPayRes.count ?? 0,
    };
  },
};
